import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { setCurrency as setCurrencyAction } from 'common/actions/format';
import * as messages from 'common/constants/messages/validations';
import { Currency, DispatchFormat, Format, Units } from 'common/types/format';
import { MILLIONS, THOUSANDS } from 'components/FeaturedSpreadsheet/constants';
import { useCurrencies } from 'components/Grid/GridFormatter/useCurrencies';
import { useLoggedMemo } from 'debug/useLogged';
import { useResponse } from 'services/hooks';
import { getExchangeRate } from 'utilities';

interface UseCurrencySelectorParams {
  format: Format | null;
  dispatch: DispatchFormat;
  fallbackMeasurementDateId?: string | number;
  fallbackSourceCurrency?: string;
  marketDate?: string;
}

const useCurrencySelector = ({
  format,
  dispatch,
  fallbackMeasurementDateId,
  fallbackSourceCurrency,
  marketDate,
}: UseCurrencySelectorParams) => {
  const [isCurrencyLoading, setIsCurrencyLoading] = useState(false);

  const [currencyMenuAnchorEl, setCurrencyMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { currencies, usedCurrencies, showMore, showLess } = useCurrencies();
  const { errorNotification } = useResponse();

  const handleCurrencyMenuOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => setCurrencyMenuAnchorEl(event.currentTarget),
    []
  );

  const handleCurrencyMenuClose = useCallback(() => setCurrencyMenuAnchorEl(null), []);

  const handleCurrencyChange = useCallback(
    async (selectedCurrency: Currency) => {
      const measurementDateId = format?.currency?.measurementDateId ?? fallbackMeasurementDateId;
      if (!measurementDateId) {
        return;
      }
      const sourceCurrency = format?.currency?.sourceCurrency ?? fallbackSourceCurrency;
      if (!sourceCurrency) {
        return;
      }
      handleCurrencyMenuClose();
      setIsCurrencyLoading(true);
      const currencyDate = marketDate && marketDate !== '' ? marketDate : undefined;
      try {
        const exchangeRate = await getExchangeRate(
          String(sourceCurrency),
          String(selectedCurrency.code),
          String(measurementDateId),
          currencyDate
        );
        let updatedAdditionalCurrencies;
        if (format?.currency?.additionalCurrencies) {
          updatedAdditionalCurrencies = await Promise.all(
            format.currency.additionalCurrencies.map(async additionalCurrency => {
              const additionalExchangeRate = await getExchangeRate(
                String(additionalCurrency.sourceCurrency),
                String(selectedCurrency.code),
                String(measurementDateId),
                currencyDate
              );
              return { ...additionalCurrency, exchangeRate: Number(additionalExchangeRate) };
            })
          );
        }
        dispatch(setCurrencyAction({
          ...selectedCurrency,
          exchangeRate: Number(exchangeRate),
          marketDate,
          additionalCurrencies: updatedAdditionalCurrencies,
        }));
      } catch {
        errorNotification(messages.GET_EXCHANGE_RATE_ERROR);
      } finally {
        setIsCurrencyLoading(false);
      }
    },
    [dispatch, errorNotification, fallbackMeasurementDateId, fallbackSourceCurrency, format, handleCurrencyMenuClose, marketDate]
  );

  // When the market date changes, re-fetch exchange rates for formats that carry
  // additionalCurrencies (i.e. GPC) so ExchangeRatesReferenceTable stays in sync.
  useEffect(() => {
    if (format?.currency?.additionalCurrencies && marketDate && format.currency.marketDate !== marketDate) {
      handleCurrencyChange(format.currency as Currency);
    }
  }, [format, handleCurrencyChange, marketDate]);

  const handleUnitClick = useCallback(() => {
    const NUMBER_UNITS: Units[] = ['' as Units, THOUSANDS as Units, MILLIONS as Units];
    const currentIndex = NUMBER_UNITS.indexOf((format?.currency?.units ?? '') as Units);
    const nextIndex = (currentIndex + 1) % NUMBER_UNITS.length;
    dispatch(setCurrencyAction({ units: NUMBER_UNITS[nextIndex] }));
  }, [dispatch, format]);

  const currencyDisplayLabel = useLoggedMemo(() => {
    const { code, sourceCurrency, exchangeRate } = format?.currency ?? {};
    if (code && sourceCurrency && code !== sourceCurrency) {
      const rate = exchangeRate ? Math.round(exchangeRate * 100) / 100 : '?';
      return `1 ${String(sourceCurrency)} → ${rate} ${String(code)}`;
    }
    return String(code ?? 'USD');
  }, [format]);

  const currencyTooltipTitle = useMemo(() => {
    const { code, sourceCurrency, exchangeRate } = format?.currency ?? {};
    if (code && sourceCurrency && code !== sourceCurrency && exchangeRate) {
      return `1 ${String(sourceCurrency)} → ${exchangeRate} ${String(code)}`;
    }
    return undefined;
  }, [format]);

  const unitDisplayLabel = useMemo(() => {
    const { symbol = '$', units } = format?.currency ?? {};
    if (units === THOUSANDS) {
      return `(${symbol}) Thousands`;
    }
    if (units === MILLIONS) {
      return `(${symbol}) Millions`;
    }
    return symbol;
  }, [format]);

  return {
    isCurrencyLoading,
    currencyMenuAnchorEl,
    handleCurrencyMenuOpen,
    handleCurrencyMenuClose,
    handleCurrencyChange,
    handleUnitClick,
    currencyDisplayLabel,
    currencyTooltipTitle,
    unitDisplayLabel,
    currencies,
    usedCurrencies,
    showMore,
    showLess,
  };
};

export default useCurrencySelector;
